/**
 * Kuromoji 分词器全局单例管理器
 * 
 * 解决每次进入播放页面都重新加载字典文件的问题：
 * - 应用启动时初始化一次
 * - 所有组件共享同一个 tokenizer 实例
 * - 避免重复的 HTTP 请求和字典文件加载
 */

// 使用 kuromoji 库的类型定义
import type { Tokenizer } from 'kuromoji';

type KuromojiTokenizer = Tokenizer;

class KuromojiManager {
  private static instance: KuromojiManager | null = null;
  private tokenizer: KuromojiTokenizer | null = null;
  private isInitializing: boolean = false;
  private initPromise: Promise<KuromojiTokenizer | null> | null = null;
  private ready: boolean = false;

  private constructor() {
    // 私有构造函数，确保单例
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): KuromojiManager {
    if (!KuromojiManager.instance) {
      KuromojiManager.instance = new KuromojiManager();
    }
    return KuromojiManager.instance;
  }

  /**
   * 初始化 Kuromoji 分词器
   * 如果已经初始化或正在初始化，会复用已有的实例或 Promise
   * 
   * @returns Promise<KuromojiTokenizer | null> tokenizer 实例，失败时返回 null
   */
  public async initialize(): Promise<KuromojiTokenizer | null> {
    // 如果已经初始化完成，直接返回
    if (this.ready && this.tokenizer) {
      return this.tokenizer;
    }

    // 如果正在初始化，返回同一个 Promise（避免重复初始化）
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    // 开始初始化
    this.isInitializing = true;
    this.initPromise = this._doInitialize();

    try {
      const tokenizer = await this.initPromise;
      this.ready = true;
      return tokenizer;
    } catch (error) {
      console.error('[KuromojiManager] 初始化失败:', error);
      this.isInitializing = false;
      this.initPromise = null;
      return null;
    }
  }

  /**
   * 实际执行初始化的方法
   */
  private async _doInitialize(): Promise<KuromojiTokenizer | null> {
    try {
      // 动态导入 kuromoji（使用动态导入避免阻塞）
      const kuromojiModule = await import('kuromoji');
      const kuromoji = kuromojiModule.default || kuromojiModule;

      // 字典路径：使用 public/dict/（在开发和生产环境都可以访问）
      const dictPath = '/dict/';

      // 使用 Promise 包装 kuromoji 的 callback 风格 API
      return new Promise<KuromojiTokenizer | null>((resolve, reject) => {
        kuromoji.builder({ dicPath: dictPath }).build((err: Error | null, tokenizer: KuromojiTokenizer) => {
          if (err) {
            console.error('[KuromojiManager] Kuromoji 构建失败:', err);
            resolve(null); // 失败时返回 null，不抛出错误，允许应用继续运行
            return;
          }
          this.tokenizer = tokenizer;
          console.log('[KuromojiManager] Kuromoji 初始化成功');
          resolve(tokenizer);
        });
      });
    } catch (error) {
      console.error('[KuromojiManager] 导入 kuromoji 失败:', error);
      return null;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * 获取 tokenizer 实例（同步）
   * 注意：如果还未初始化完成，会返回 null
   * 
   * @returns KuromojiTokenizer | null
   */
  public getTokenizer(): KuromojiTokenizer | null {
    return this.tokenizer;
  }

  /**
   * 检查是否已就绪
   * 
   * @returns boolean
   */
  public isReady(): boolean {
    return this.ready && this.tokenizer !== null;
  }

  /**
   * 等待初始化完成（如果正在初始化）
   * 
   * @returns Promise<boolean> 是否初始化成功
   */
  public async waitForReady(): Promise<boolean> {
    if (this.isReady()) {
      return true;
    }

    if (this.initPromise) {
      const tokenizer = await this.initPromise;
      return tokenizer !== null;
    }

    // 如果还没有开始初始化，启动初始化
    const tokenizer = await this.initialize();
    return tokenizer !== null;
  }
}

/**
 * 获取全局 Kuromoji 管理器实例
 */
export function getKuromojiManager(): KuromojiManager {
  return KuromojiManager.getInstance();
}

/**
 * 初始化 Kuromoji（在应用启动时调用）
 * 非阻塞：在后台异步初始化，不阻塞应用启动
 */
export function initKuromoji(): void {
  const manager = getKuromojiManager();
  // 异步初始化，不等待完成
  manager.initialize().catch((error) => {
    console.error('[initKuromoji] 初始化失败:', error);
  });
}

